import type { Coach } from '@entities/coaches/index.ts';
import { AxiosInstance } from 'axios/index';
import type { PublicationType } from '@widgets/shared/Publication/types.ts';
import {
  type Article,
  type ArticleEditValues,
  type ArticlesPaginatedList,
} from '@shared/types/articles';
import { ActiveTabType } from '@shared/types/common';
import { EntityByIdParam, ModuleParam } from '@src/api/types.ts';

const articlesUrl = '/articles/';

export class ArticlesApi {
  constructor(private instance: AxiosInstance) {}

  public async getList({ tabName, ...params }: ArticlesListParams) {
    return this.instance
      .get<ArticlesPaginatedList>(
        articlesUrl + `${tabName === 'all' ? `${tabName}/` : `all/${tabName}`}`,
        {
          params,
        }
      )
      .then((res) => res.data);
  }

  public async getById({ id }: EntityByIdParam) {
    return this.instance.get<Article>(articlesUrl + `all/${id}/`).then((res) => res.data);
  }

  public async getRecommended({ hash }: GetRecommendedArticleParams) {
    return this.instance
      .get<GetRecommendedArticleResponse>(articlesUrl + `all/${hash}/subscribe`)
      .then((res) => res.data);
  }

  public async recommend({ id, body }: RecommendArticleDto) {
    return this.instance.post<{ hashcode: string }>(articlesUrl + `all/${id}/recommend/`, body);
  }

  public async create(body: ArticleEditValues) {
    return this.instance.post<Article>(articlesUrl + `all/`, body);
  }

  public async update({ id, ...body }: UpdateArticleDto) {
    return this.instance.patch<Article>(articlesUrl + `all/${id}/`, body);
  }

  public async updatePublication({ publicationData, id }: UpdateArticlePublicationDto) {
    return this.instance.patch<PublicationType>(
      articlesUrl + `all/${id}/publication/`,
      publicationData
    );
  }

  public async delete({ id }: EntityByIdParam) {
    return this.instance.delete<void>(articlesUrl + `all/${id}/`);
  }

  public async makeFavorite({ id }: EntityByIdParam) {
    return this.instance.post<void>(articlesUrl + `/favorite/`, { id });
  }

  public async deleteFromFavorite({ id }: EntityByIdParam) {
    return this.instance.delete<void>(articlesUrl + `/favorite/${id}/`);
  }

  public async like({ id }: EntityByIdParam) {
    return this.instance.post<void>(articlesUrl + `all/${id}/like/`);
  }

  public async removeLike({ id }: EntityByIdParam) {
    return this.instance.delete<void>(articlesUrl + `all/${id}/like/`);
  }
}

export type ArticlesListParams = Partial<
  ModuleParam & {
    tags: string;
    authors: string;
    search: string;
    page: number;
    partitions: string;
    tabName?: ActiveTabType;
  }
>;
export type UpdateArticleDto = ArticleEditValues & EntityByIdParam;
export type UpdateArticlePublicationDto = { publicationData: Partial<PublicationType>; id: number };

export type GetRecommendedArticleParams = {
  hash?: string;
};

export type GetRecommendedArticleResponse = {
  status: 'auth' | 'no_auth';
  invite: { article: Article; recommendation_comment: string; recommended_by: Coach };
};

export type RecommendArticleDto = { id: number; body: { comment?: string } };
